import { useEffect, useState } from 'react'

/**
 * The watermark's storage key.
 *
 * Scoped per user id, because localStorage is per-ORIGIN and this app is one
 * origin for everybody: two accounts used on one phone — a shared household
 * device, or a support session — would otherwise share a single watermark, so
 * signing in as the second person would mark their feed read up to whenever the
 * first person last looked at theirs. That is the one failure of this design
 * that produces a WRONG answer rather than a merely absent one, so it is worth
 * the suffix.
 *
 * The unscoped fallback exists only for the window before the profile lands in
 * the store. It is never written under (see the hook), so it cannot become a
 * shared bucket by accident.
 */
const KEY_PREFIX = 'vera:activity:last-seen-at'

const storageKeyFor = (userId?: string) =>
  userId ? `${KEY_PREFIX}:${userId}` : KEY_PREFIX

/**
 * Epoch milliseconds, or `null` for "no usable watermark".
 *
 * Every failure — Safari private mode throwing on `getItem`, a hand-edited
 * value, a key written by an older shape of this feature — degrades to `null`,
 * which the caller reads as "treat nothing as seen" and so renders no divider.
 * Failing to a NO-divider state is the safe direction: a missing mark costs the
 * reader nothing, while a wrong one tells them events they have never read are
 * already handled.
 */
const readWatermark = (key: string): number | null => {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null

    const parsed = Number(raw)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  } catch {
    return null
  }
}

const writeWatermark = (key: string, at: number) => {
  try {
    window.localStorage.setItem(key, String(at))
  } catch {
    // Quota, private mode, storage disabled. Nothing to recover — the next
    // visit simply reads no watermark and shows no divider, which is the same
    // degradation as a first-ever visit.
  }
}

/**
 * The timestamp at which this reader last had the Activity feed on screen, as
 * of the PREVIOUS visit — which is the only value that can mark anything.
 *
 * ## Why localStorage and not a server field
 *
 * A per-user `activity_last_seen_at` column, written by a `POST
 * /certificates/customer/activity/seen`, is the durable version of this and is
 * its own task. It is not what shipped here, deliberately: this needs no schema
 * change, no endpoint, no migration, and no extra request on a screen whose
 * whole job is to render fast.
 *
 * Two real costs come with that, and neither is hypothetical:
 *
 * 1. **It resets when storage is cleared.** Clearing site data, "Remove All
 *    Website Data", or an iOS Safari eviction of an unused origin all wipe the
 *    watermark. The next visit then reads `null` and shows no divider at all —
 *    the reader loses the mark rather than getting a wrong one, which is why
 *    this cost is tolerable, but they do lose it silently.
 *
 * 2. **It does not agree across devices.** The watermark is per-origin,
 *    per-browser. Someone who reads their feed on a phone and then opens the
 *    same account on a tablet sees EVERYTHING as new there, because that
 *    browser has never written a watermark. Two devices is the normal case for
 *    this product — a certificate is reached by tapping a tag with a phone, but
 *    the account is just as often opened on a laptop — so this is a limitation
 *    a real reader will meet, not an edge case.
 *
 * Both are arguments for the server field eventually. Neither is an argument
 * against a quiet wayfinding mark that is right most of the time and absent,
 * rather than wrong, the rest of it.
 *
 * ## Read before write, always
 *
 * The mark has to be computed against the value from the PREVIOUS visit, so the
 * read happens during render and the write in an effect after it. Written
 * first, the watermark would equal "now" and no row could ever be newer than
 * it — the divider would never appear and the bug would look like the feature
 * simply not working.
 *
 * ## Written on MOUNT, not on scroll
 *
 * "Seen" here means "the feed was opened", not "these specific rows entered the
 * viewport". Marking per row — an IntersectionObserver writing a high-water
 * mark as rows scroll past — is more literally true and worse in use: the
 * watermark would then depend on how far the reader happened to scroll, so
 * closing the tab at row three leaves rows four onward flagged new forever,
 * and the divider walks down the list a few rows per visit instead of
 * resetting. Opening the feed is the act being recorded, and it is the one the
 * reader would describe as "I've looked at that".
 *
 * @param userId The signed-in reader's profile id, if the store has it yet.
 */
export const useActivityLastSeen = (userId?: string): number | null => {
  const storageKey = storageKeyFor(userId)

  /*
   * Read SYNCHRONOUSLY on the first render for this key rather than in an
   * effect. An effect would leave the first paint with no watermark, so the
   * divider would land one frame after the rows it sits between — a line
   * materialising inside a list the reader is already looking at reads as a
   * glitch, not as a mark.
   */
  const [seen, setSeen] = useState(() => ({
    key: storageKey,
    at: readWatermark(storageKey),
  }))

  /*
   * The profile arrives from the store a beat after the page mounts, so the key
   * changes from unscoped to scoped mid-render. Re-derive during render (React's
   * supported "adjust state when props change" path) rather than in an effect:
   * on the paint in between, the scoped watermark is what applies, and using the
   * unscoped one for even one frame would mark the wrong rows.
   */
  if (seen.key !== storageKey) {
    setSeen({ key: storageKey, at: readWatermark(storageKey) })
  }

  useEffect(() => {
    /*
     * Never write under the unscoped key. Doing so would create exactly the
     * shared bucket the per-user scoping exists to prevent — the first visit of
     * every account on a device would stamp it, and every later account would
     * inherit the stamp.
     */
    if (!userId) return

    writeWatermark(storageKey, Date.now())
  }, [storageKey, userId])

  // During the throwaway render that follows the re-derive above, `seen` still
  // holds the previous key's value; report no watermark rather than that one.
  return seen.key === storageKey ? seen.at : null
}
